import React from 'react';
import PropTypes from 'prop-types';
import { useClassName, useWindowSize } from 'common/hooks';
import useCustomStyle from '../../hooks';
import Card from '../Card';
// import Section from '../../Section'; // Circular dependency fixed by using prop

import './grid.less';

const hasValue = (value) =>
  value !== undefined && value !== null && value !== '';

// Same responsive resolution as useCustomStyle, for a single setting of any entry
function getSetting(settings, key, viewport) {
  const fields = settings?.find((s) => s.fields.key === key)?.fields;
  if (!fields) return undefined;

  const value =
    (viewport.isMobile && fields.mobileValue) ||
    (viewport.isTablet && fields.tabletValue) ||
    (viewport.isDesktop && fields.desktopValue) ||
    fields.desktopValue ||
    fields.tabletValue ||
    fields.mobileValue ||
    fields.value;

  return hasValue(value) ? value : undefined;
}

// Cards mode: each card picks its column (--grid-column) and position inside it (--grid-order),
// both per breakpoint, so columns, ordering and heights are fully editable from Contentful.
// Order is strict per breakpoint (no fallback across them): a card with only a mobile order keeps
// its natural (entry) order on desktop.
function getOrder(settings, viewport) {
  const fields = settings?.find((s) => s.fields.key === '--grid-order')?.fields;
  if (!fields) return NaN;

  const value = viewport.isMobile
    ? fields.mobileValue
    : viewport.isTablet
    ? fields.tabletValue || fields.desktopValue
    : fields.desktopValue;

  return hasValue(value) ? parseFloat(value) : NaN;
}

function groupByColumn(items, viewport) {
  const columns = {};

  items.forEach((item, position) => {
    const settings = item.fields.settings;
    const column =
      parseInt(getSetting(settings, '--grid-column', viewport), 10) || 1;
    const order = getOrder(settings, viewport);

    columns[column] = columns[column] || [];
    columns[column].push({
      item,
      order: Number.isNaN(order) ? position : order,
      position,
    });
  });

  return Object.keys(columns)
    .map(Number)
    .sort((a, b) => a - b)
    .map((column) =>
      columns[column].sort(
        (a, b) => a.order - b.order || a.position - b.position
      )
    );
}

export default function Grid({
  component,
  rawSection,
  refs,
  venue,
  handleTrack,
  index,
  SectionComponent,
}) {
  const classNames = useClassName('clp-grid');
  const [setRef, style] = useCustomStyle(component.fields.settings);
  const viewport = useWindowSize();

  const columns = style['--columns'] || '1fr';
  const gap = style['--gap'] || '20px';
  const items = component.fields.items || [];
  const sections = component.fields.sections || [];
  // Get raw sections if available, assuming structure matches
  const rawSections = rawSection?.fields?.sections?.['en-US'] || [];

  const getLayout = (settings) => {
    if (!settings) return {};
    const layout = {};

    const gridColumn = getSetting(settings, '--grid-column', viewport);
    if (gridColumn) layout.gridColumn = gridColumn;

    const gridRow = getSetting(settings, '--grid-row', viewport);
    if (gridRow) layout.gridRow = gridRow;

    const alignSelf = getSetting(settings, '--align-self', viewport);
    if (alignSelf) {
      layout.alignSelf = alignSelf;
      layout.height = 'auto';
    }

    return layout;
  };

  if (items.length) {
    return (
      <div
        ref={setRef}
        className={classNames(['container', 'container--cards'])}
        style={{
          ...style,
          gridTemplateColumns: columns,
          gap: gap,
          ...(style['--column-gap'] && { columnGap: style['--column-gap'] }),
          ...(style['--max-width'] && { maxWidth: style['--max-width'] }),
          ...(style['--padding-top'] && { paddingTop: style['--padding-top'] }),
          ...(style['--padding-bottom'] && {
            paddingBottom: style['--padding-bottom'],
          }),
        }}>
        {groupByColumn(items, viewport).map((column, idx) => (
          <div
            key={idx}
            className={classNames('stack')}
            style={{ gap: style['--row-gap'] || gap }}>
            {column.map(({ item }) => (
              <Card
                key={item.sys.id}
                item={item}
                venue={venue}
                refs={refs}
                handleTrack={handleTrack}
              />
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      ref={setRef}
      className={classNames('container')}
      style={{
        ...style,
        gridTemplateColumns: columns,
        gap: gap,
      }}>
      {sections.map((section, idx) => (
        <div
          key={idx}
          className={classNames('column')}
          style={getLayout(section.fields.settings)}>
          {SectionComponent ? (
            <SectionComponent
              section={section}
              rawSection={rawSections[idx]} // Pass raw section if needed mainly for forms
              refs={refs}
              index={`${index}-${idx}`} // Create unique index for nested refs
            />
          ) : null}
        </div>
      ))}
    </div>
  );
}

Grid.propTypes = {
  component: PropTypes.object.isRequired,
  rawSection: PropTypes.object,
  refs: PropTypes.object,
  index: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

Grid.defaultProps = {
  refs: {},
};
